import React from "react";
import { useQuery } from "@apollo/client";
import { gql } from "graphql-tag";
import { Grid, Button, ListSubheader } from "@mui/material";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import { SelectAutocomplete } from "components/haven-design/form/select";
import { Viewer } from "components/auth/types";
import {
  getTemplateGroup_templateGroup as Template,
} from "types/gql-types";

import { apolloClient } from "data/graphql";

const TEMPLATES = gql`
  query myTemplateGroups {
    templateGroups {
      id
      name
      userId
      templates {
        id
        subject
        body
        type
        isEmpty
      }
    }
  }
`;

const sortByTemplateName = (first: Template, second: Template) => {
  const nameA = first.name.toLowerCase();
  const nameB = second.name.toLowerCase();
  if (nameA < nameB) return -1;
  if (nameA > nameB) return 1;
  return 0;
};

const MANAGE_TEMPLATES_HREF = "/dash/manager/settings/templates";

type Props = {
  onChange(template: Template | null): void;
  viewer: Viewer;
  currentTemplate?: Template | null;
  className?:string;
}

const CustomListbox = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
  ({ children, ...other }, ref) => (
    <ul ref={ref} {...other} style={{ padding: 0 }}>
      <ListSubheader>
        <Button
          fullWidth
          href={MANAGE_TEMPLATES_HREF}
          sx={{ justifyContent: "flex-start", textTransform: "none" }}>
          Manage Templates...
        </Button>
      </ListSubheader>
      {children}
    </ul>
  ),
);
CustomListbox.displayName = "CustomListbox";

export const TemplateSelector:React.FC<Props> = (props) => {
  const [sortedTemplates, setTemplates] = React.useState([] as Template[]);
  const { viewer } = props;
  const { data, loading, error } = useQuery(TEMPLATES, {
    fetchPolicy: "no-cache",
    variables: {
      orgId: viewer.organization?.id ?? null,
    },
    client: apolloClient,
  });

  const templates = data?.templateGroups || [] as Template[];

  React.useEffect(() => {
    setTemplates([...templates].sort(sortByTemplateName));
  }, [data]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ServerError desc={error.message} />;
  if (!data) return <ServerError desc="Missing Data" />;

  const selectedValue = props?.currentTemplate;

  return (
    <Grid size={{ xs: 12, sm: 6, md: 5 }} className={props.className}>
      <SelectAutocomplete
        label="Select Template"
        value={selectedValue}
        onChange={(_event, value) => {
          props.onChange(value);
        }}
        options={sortedTemplates}
        getOptionLabel={({ name }: Template) => name}
        ListboxComponent={(listboxProps) => (
          <CustomListbox
            {...listboxProps}
          />
        )}
      />
    </Grid>
  );
};
